import { observable, action, flow, makeObservable } from 'mobx';
import { isSecureLinkError } from '@roc-capital/secure-link';
import { LoanFastrackDocumentStore } from './loanFastrackDocumentStore';
import { GlobalStore } from '@roc/feature-app-core';
import { SecureLinkLoanFastrackService, SecureLinkRequest } from '../services/secureLinkLoanFastrackService';

const noSession: SecureLinkRequest = () =>
  Promise.reject(new Error('LoanFastrackPaymentsStore: call setRequest() with the session request first'));

/**
 * The Fast Track payment page (/link/loan-fastrack-payment). Every call goes to loan-service's session-guarded
 * endpoints; the Fast Track comes from the secure link session on the server.
 *
 * Created with the app's root store, before there is a session: the page passes the session's request in with
 * setRequest() once useSecureLinkSession has one. Until then every call rejects.
 */
export class LoanFastrackPaymentsStore {
  private globalStore: GlobalStore;
  private secureLinkService: SecureLinkLoanFastrackService;
  public loanFastrackDocumentStore: LoanFastrackDocumentStore;
  private cardHolderName: string = null;
  docusignStatus: boolean = false;
  paymentStatus: boolean = false;

  constructor(globalStore) {
    this.globalStore = globalStore;
    this.loanFastrackDocumentStore = new LoanFastrackDocumentStore(globalStore);
    this.setRequest(noSession);
    makeObservable(this, {
      docusignStatus: observable,
      fetchDocusignAndPaymentStatus: flow,
      generateFastTrackDocument: flow,
      paymentStatus: observable,
      setDocusignStatus: action,
      setPaymentStatus: action,
      reset: action,
    });
  }

  /** The authorized fetch from useSecureLinkSession; every call goes through it. */
  setRequest(request: SecureLinkRequest) {
    this.secureLinkService = new SecureLinkLoanFastrackService(request);
    this.loanFastrackDocumentStore.setSecureLinkService(this.secureLinkService);
  }

  setDocusignStatus(status: boolean) {
    this.docusignStatus = status;
  }

  setPaymentStatus(status: boolean) {
    this.paymentStatus = status;
  }

  setCardHolderName(cardHolderName: string) {
    this.cardHolderName = cardHolderName;
  }

  *generateFastTrackDocument(signatureDataURL: string) {
    try {
      yield this.secureLinkService.sign({
        cardHolderName: this.cardHolderName,
        signature: signatureDataURL,
        userAgent: navigator.userAgent,
      });
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Fast Track document signed successfully!',
      });
      this.setDocusignStatus(true);
      return true;
    } catch (error) {
      if (isSecureLinkError(error) && error.kind === 'COMPLETED') {
        // Already paid: the link is done.
        this.setPaymentStatus(true);
        return false;
      }
      if (isSecureLinkError(error) && error.kind === 'INVALID') {
        // The page switches to "session ended"; no toast needed.
        return false;
      }
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error signing Fast Track document. Please try again.',
      });
      return false;
    }
  }

  *fetchDocusignAndPaymentStatus() {
    try {
      const data = yield this.secureLinkService.getData();
      if (data) {
        this.docusignStatus = data.signed;
        this.paymentStatus = data.paid;
      }
    } catch (error) {
      if (isSecureLinkError(error) && error.kind === 'COMPLETED') {
        this.paymentStatus = true;
        return;
      }
      if (isSecureLinkError(error) && error.kind === 'INVALID') {
        return;
      }
      this.globalStore.notificationStore.showErrorNotification({
        message: 'There was an error while retrieving payment status',
      });
    }
  }

  reset() {
    this.docusignStatus = false;
    this.paymentStatus = false;
    this.cardHolderName = null;
  }
}

export default LoanFastrackPaymentsStore;
